import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coffee, ShoppingCart, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import RecentlyViewed, { addToRecentlyViewed } from '../components/RecentlyViewed';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RECENTLY_VIEWED_KEY = 'recentlyViewedMuggs';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('muggs') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

// Coaster variants pricing
const COASTER_VARIANTS = [
  { id: 'single', label: 'Single', price: 7 },
  { id: 'set-2', label: 'Set of 2', price: 12 },
  { id: 'set-4', label: 'Set of 4', price: 21 }
];

// The mugg's photos (first = main) plus its optional spin video. Extra views
// and the flat artwork are letterboxed rather than cropped; the video only
// loads when someone taps its thumbnail.
function ProductGallery({ product, outOfStock, onInteract }) {
  const photos = product.images?.length ? product.images : [product.image_url];
  const slides = [
    ...photos.map((url) => ({ type: 'photo', url })),
    ...(product.video_url ? [{ type: 'video', url: product.video_url }] : [])
  ];
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const current = slides[index] || slides[0];

  const show = (next) => {
    setIndex((next + slides.length) % slides.length);
    if (onInteract) onInteract();
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null || slides.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
  };

  return (
    <div>
      <div
        className="product-image-container"
        style={{ position: 'relative' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            className="product-image product-image-fit"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={current.url}
            alt={index === 0 ? product.title : `${product.title}, view ${index + 1}`}
            className={`product-image${index === 0 ? '' : ' product-image-fit'}`}
          />
        )}
        {outOfStock && (
          <div style={{ position: 'absolute', top: '10px', left: '10px', background: '#dc2626', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
            Out of Stock
          </div>
        )}
      </div>
      {slides.length > 1 && (
        <div className="product-thumbs" data-testid={`thumbs-${product.id}`}>
          {slides.map((slide, i) => (
            <button
              key={`${slide.url}-${i}`}
              type="button"
              className={`product-thumb${i === index ? ' active' : ''}`}
              onClick={() => show(i)}
              aria-label={slide.type === 'video' ? 'Play video' : `Show view ${i + 1}`}
            >
              {slide.type === 'video' ? (
                <span className="product-thumb-video"><Play size={18} fill="currentColor" /></span>
              ) : (
                <img src={slide.url} alt="" loading="lazy" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onView }) {
  const [selectedVariant, setSelectedVariant] = useState(
    product.has_variants ? COASTER_VARIANTS[0] : null
  );
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const { addItem } = useCart();

  const currentPrice = product.has_variants
    ? selectedVariant?.price
    : product.price;

  const outOfStock = product.stock === 0;

  const trackView = () => {
    if (!hasTrackedView && onView) {
      onView(product);
      setHasTrackedView(true);
    }
  };

  const handleAddToCart = () => {
    trackView();
    addItem({
      shop: 'muggs',
      product_id: product.id,
      product_title: product.title,
      image_url: product.image_url,
      variant: selectedVariant?.id || null,
      variant_label: selectedVariant?.label || null,
      price: currentPrice,
      quantity: 1,
      max_quantity: product.stock,
      special_instructions: specialInstructions || null
    });
    toast.success(`${product.title} added to cart!`);
  };

  return (
    <div className="muggs-product-card" data-testid={`product-${product.id}`}>
      <ProductGallery product={product} outOfStock={outOfStock} onInteract={trackView} />
      <div className="product-details">
        <h3 className="product-title">{product.title}</h3>
        {product.description && (
          <p className="product-description">{product.description}</p>
        )}

        {product.has_variants && (
          <div className="variant-selector">
            <label htmlFor={`variant-${product.id}`}>Quantity:</label>
            <select
              id={`variant-${product.id}`}
              value={selectedVariant?.id}
              onChange={(e) => {
                const variant = COASTER_VARIANTS.find(v => v.id === e.target.value);
                setSelectedVariant(variant);
              }}
              className="variant-dropdown"
              data-testid={`variant-select-${product.id}`}
            >
              {COASTER_VARIANTS.map(variant => (
                <option key={variant.id} value={variant.id}>
                  {variant.label} — ${variant.price}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="product-price">
          ${currentPrice?.toFixed(2)}
        </div>

        <div className="special-instructions">
          <label htmlFor={`instructions-${product.id}`}>
            Special Instructions or Personal Note (optional)
          </label>
          <textarea
            id={`instructions-${product.id}`}
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Gift message, special requests, etc."
            className="instructions-textarea"
            rows={3}
          />
        </div>

        <Button
          className="buy-now-btn"
          onClick={handleAddToCart}
          disabled={outOfStock}
          data-testid={`buy-btn-${product.id}`}
        >
          <ShoppingCart size={18} />
          {outOfStock ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </div>
    </div>
  );
}

function Muggs() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { itemCount, openCart } = useCart();

  useEffect(() => {
    fetchProducts();
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (stored) {
      try {
        setRecentlyViewed(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);

  const handleProductView = (product) => {
    const updated = addToRecentlyViewed(RECENTLY_VIEWED_KEY, product);
    setRecentlyViewed(updated);
  };

  const handleRecentItemClick = (item) => {
    const element = document.querySelector(`[data-testid="product-${item.id}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.boxShadow = '0 0 0 3px #1a1a1a';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/muggs-products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Hero Section */}
      <section className="muggs-hero">
        <div className="hero-content" style={{ textAlign: 'center' }}>
          <img
            src="https://daily-obx-cdn.b-cdn.net/newsletter-images/bbmuggs-logo.png"
            alt="B.B. Muggs - Distinctive Drinkware & Stuff"
            className="shop-hero-logo"
            style={{ maxWidth: '400px', marginBottom: '20px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
          <p className="hero-description">
            Life's too short for boring mugs. Coffee tastes better with a little salt air
            and a proper mugg designed by the legendary OBX Beach Bum. It just does.
          </p>
        </div>
      </section>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && !loading && (
        <RecentlyViewed
          storageKey={RECENTLY_VIEWED_KEY}
          onItemClick={handleRecentItemClick}
          title="Recently Viewed"
        />
      )}

      {/* Products Grid */}
      <main className="muggs-main">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p>Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Coffee size={64} style={{ color: '#ccc', marginBottom: '20px' }} />
            <h3 style={{ margin: '0 0 10px' }}>Coming Soon!</h3>
            <p style={{ color: '#666' }}>Our B.B. Muggs collection is being prepared. Check back soon!</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} onView={handleProductView} />
            ))}
          </div>
        )}

        <div className="shipping-info">
          <p>🚚 Free shipping on orders over $30 (2+ mugs) • US shipping only</p>
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default Muggs;
